import type {DateRange} from "@daypicker/react";
import type {Column} from "@tanstack/react-table";
import {addDays, addMonths, parseISO} from "date-fns";
import React, {useEffect, useState} from "react";
import {DateRangeCalendar} from "@/components/ui/DateRangePicker";
import {DataTableFilterBase} from "@/components/ui/data-table-filter-base";

interface DataTableDateFilterProps {
  column: Column<any, unknown>;
  allowFutureDates?: boolean;
}

export const DataTableDateFilter = ({column, allowFutureDates = false}: DataTableDateFilterProps) => {
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const filterValue = column.getFilterValue();

  useEffect(() => {
    if (filterValue === undefined) {
      setDateRange((current) => current === undefined ? current : undefined);
    };
  }, [filterValue]);

  const applyFilter = (range: DateRange | undefined) => {
    setDateRange(range);
    if (range) {
      column.setFilterValue({
        startDate: range.from,
        endDate: range.to,
      });
    } else {
      column.setFilterValue(undefined);
    }
  };

  const handleClearFilter = () => {
    setDateRange(undefined);
    if (filterValue !== undefined) {
      column.setFilterValue(undefined);
    }
  };

  const facets = column?.getFacetedUniqueValues();
  
  // Get unique date values and convert them to Date objects.
  const dates = Array.from(facets.keys())
    .map(dateStr => {
      try {
        if (typeof dateStr === 'string') {
          // First try to parse as ISO string
          try {
            const parsedDate = parseISO(dateStr);
            if (!isNaN(parsedDate.getTime())) {
              return parsedDate;
            }
          } catch {
            // If it's not an ISO string, try as regular date
            const date = new Date(dateStr);
            if (!isNaN(date.getTime())) {
              return date;
            }
          }
        } else if (dateStr instanceof Date) {
          return dateStr;
        } else {
          // Try as timestamp or other format
          const date = new Date(dateStr as any);
          if (!isNaN(date.getTime())) {
            return date;
          }
        }
      } catch (e) {
        console.error("Error parsing date:", e);
      }
      return null;
    })
    .filter(date => date && !isNaN((date as Date).getTime())) as Date[];
  
  // Find the min and max date if we have dates
  const minDate = dates.length > 0 
    ? new Date(Math.min(...dates.map(date => date.getTime())))
    : new Date(new Date().getFullYear() - 1, 0, 1); // Default to last year if no dates
  
  // Add a full day to maxDate to ensure it includes the entire day
  const maxDate = dates.length > 0
    ? (() => {
        const date = new Date(Math.max(...dates.map(date => date.getTime())));
        // Ensure maxDate includes the entire day by setting to end of day
        date.setHours(23, 59, 59, 999);
        return date;
      })()
    : (allowFutureDates 
        ? new Date(new Date().getFullYear() + 2, 11, 31) // Allow dates up to 2 years in the future
        : new Date()); // Default to today if no dates

  return (
    <div className="flex flex-col gap-2">
      <DataTableFilterBase filterCount={dateRange ? 1 : 0} className="w-auto p-0" align="start">
        <DateRangeCalendar
          value={dateRange}
          onChange={applyFilter}
          initialMonth={allowFutureDates ? new Date() : dates.length > 0 ? addMonths(maxDate, -1) : new Date()}
          disabledDates={(date) =>
            allowFutureDates ? date < addDays(minDate, -1) : date > addDays(new Date(), 1)
          }
          footerPresets={
            allowFutureDates
              ? ["today", "next7Days", "next30Days", "next90Days"]
              : ["today", "yesterday", "last7Days", "last30Days"]
          }
          showClearSelection
          showRangeSummary
          showTopControls
          onClearSelection={handleClearFilter}
        />
      </DataTableFilterBase>
    </div>
  );
};
